import { ChangeEvent, memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useContent } from 'src/contexts/ContentContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { TableCellDiv } from 'src/core/ui/TableCellDiv';
import { useListingAutoPricingSettings } from 'src/modals/ListingDetails/components/useListingAutoPricingSettings';
import {
  CheckIcon,
  CrossIcon,
  IconsFill,
  ProcessingIcon,
} from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { AutoPricingOutlierMode } from 'src/WebApiController';

import {
  ListingPricingInput,
  ListingPricingSettingsCellProps,
} from '../ListingPriceForm.types';
import { useCellDisabled, useIsSubmittingField } from '../useCellState';
import * as styles from './Cell.css';

const OutlierLimitCellRaw: React.FC<ListingPricingSettingsCellProps> = ({
  listing,
  editable,
  event,
  isV2Variant = false,
}) => {
  const { watch, formState, getFieldState, setValue } =
    useFormContext<ListingPricingInput>();

  const saveMsg = useContent(ContentId.Save);
  const cancelMsg = useContent(ContentId.Cancel);

  const { outlierMode, outlierKthLowestLimit, canAutoPrice, onOutlierChange } =
    useListingAutoPricingSettings(listing);

  const rowIndex = watch('rowIndex');
  const isDisabled = useCellDisabled(
    listing,
    canAutoPrice,
    editable,
    event.virtId,
    outlierMode !== AutoPricingOutlierMode.KthLowest
  );
  const isSubmittingField = useIsSubmittingField('outlierKthLowestLimit');

  const onUpdateOutlierLimit = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = parseInt(e.target.value);
      onOutlierChange({
        outlierModeNew: outlierMode,
        outlierKthLowestLimitNew: value,
      });
    },
    [onOutlierChange, outlierMode]
  );

  const content = (
      <PosFormField
        errors={
          getFieldState('outlierKthLowestLimit', formState)?.error?.message
        }
        showErrorsInline
        className={isV2Variant ? undefined : styles.formFieldContainer}
        style={isV2Variant ? { width: '100%' } : undefined}
        fullHeight={isV2Variant}
      >
        <PosTextField
          className={isV2Variant ? styles.formTextFiledV2 : styles.formTextFiled}
          value={outlierKthLowestLimit ?? ''}
          disabled={isDisabled}
          rootProps={isV2Variant ? { style: { border: 'none', borderRadius: 0, height: '100%' } } : undefined}
          type="number"
          min={0}
          max={100}
          onChange={onUpdateOutlierLimit}
          postfixDisplay={
            !isDisabled &&
            outlierKthLowestLimit !==
              formState.defaultValues?.outlierKthLowestLimit ? (
              <Stack direction="row" gap="s">
                <CheckIcon
                  title={saveMsg}
                  withHoverEffect
                  size={vars.iconSize.s}
                  fill={IconsFill.textSuccess}
                  onClick={(e) => {
                    e.stopPropagation();
                    setValue('isSubmittingPricingSettings', true);
                    setValue('isSubmittingRowIndex', rowIndex);
                    setValue('submittingField', 'outlierKthLowestLimit');
                  }}
                />
                <CrossIcon
                  title={cancelMsg}
                  withHoverEffect
                  size={vars.iconSize.s}
                  onClick={(e) => {
                    e.stopPropagation();
                    setValue(
                      'outlierKthLowestLimit',
                      formState.defaultValues?.outlierKthLowestLimit ?? null
                    );
                  }}
                />
              </Stack>
            ) : isSubmittingField ? (
              <div className="operation-button">
                <RotatingWrapper>
                  <ProcessingIcon size={vars.iconSize.s} />
                </RotatingWrapper>
              </div>
            ) : undefined
          }
        />
      </PosFormField>
  );

  return isV2Variant ? (
    content
  ) : (
    <TableCellDiv align="right" showTooltip={false}>
      {content}
    </TableCellDiv>
  );
};

export const OutlierLimitCell =
  memo<ListingPricingSettingsCellProps>(OutlierLimitCellRaw);
